/**
 * LocationPickerModal – välj en punkt på kartan (klicka eller dra markören),
 * visar reverse-geokodad adress och en radie. Används för att ange hem-/arbetsplats
 * för EV-laddredovisning. Sparar lat/lng, adress och radie (m).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L, { LatLng } from 'leaflet';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { reverseGeocode, searchAddress, type ForwardGeocodeResult } from '@/services/geocoding/geocodingApi';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER: [number, number] = [59.33, 18.07];
const DEFAULT_ZOOM = 14;

export interface PickedLocation {
  lat: number;
  lng: number;
  address: string | null;
  radius: number;
}

interface LocationPickerModalProps {
  title: string;
  initialLat: number | null;
  initialLng: number | null;
  initialRadius: number | null;
  initialAddress: string | null;
  /** Fallback-centrum om ingen punkt valts (t.ex. fordonets placering). */
  fallbackCenter?: { lat: number; lng: number } | null;
  defaultRadius: number;
  onSave: (loc: PickedLocation) => void;
  onClose: () => void;
}

// Leaflets standard-markörikon laddar inte alltid i bundlade appar – sätt explicit.
const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function ClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapSizer() {
  const map = useMap();
  useEffect(() => {
    const apply = () => map.invalidateSize();
    const t1 = window.setTimeout(apply, 50);
    const t2 = window.setTimeout(apply, 250);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [map]);
  return null;
}

/** Flyger kartan till en målpunkt när den ändras (t.ex. valt sökresultat). */
function MapFlyTo({ target }: { target: { lat: number; lng: number; zoom?: number } | null }) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], target.zoom ?? 16, { duration: 0.6 });
    }
  }, [map, target]);
  return null;
}

export default function LocationPickerModal({
  title,
  initialLat,
  initialLng,
  initialRadius,
  initialAddress,
  fallbackCenter,
  defaultRadius,
  onSave,
  onClose,
}: LocationPickerModalProps) {
  const { t } = useTranslation();

  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(
    initialLat != null && initialLng != null ? { lat: initialLat, lng: initialLng } : null
  );
  const [address, setAddress] = useState<string | null>(initialAddress ?? null);
  const [radius, setRadius] = useState<number>(initialRadius ?? defaultRadius);
  const [geocoding, setGeocoding] = useState(false);
  const geocodeTimer = useRef<number | null>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ForwardGeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [flyTo, setFlyTo] = useState<{ lat: number; lng: number; zoom?: number } | null>(null);
  const searchTimer = useRef<number | null>(null);

  const center: [number, number] = useMemo(() => {
    if (position) return [position.lat, position.lng];
    if (fallbackCenter) return [fallbackCenter.lat, fallbackCenter.lng];
    return DEFAULT_CENTER;
  }, [position, fallbackCenter]);

  const runReverseGeocode = useCallback((lat: number, lng: number) => {
    if (geocodeTimer.current) window.clearTimeout(geocodeTimer.current);
    geocodeTimer.current = window.setTimeout(async () => {
      setGeocoding(true);
      try {
        const res = await reverseGeocode(lat, lng);
        setAddress(res?.address ?? null);
      } catch {
        /* lämna adressen som den är */
      } finally {
        setGeocoding(false);
      }
    }, 400);
  }, []);

  const handlePick = useCallback(
    (lat: number, lng: number) => {
      setPosition({ lat, lng });
      setShowResults(false);
      runReverseGeocode(lat, lng);
    },
    [runReverseGeocode]
  );

  const runSearch = useCallback(
    (q: string) => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
      if (q.trim().length < 2) {
        setResults([]);
        setShowResults(false);
        return;
      }
      searchTimer.current = window.setTimeout(async () => {
        setSearching(true);
        try {
          const bias = position ?? fallbackCenter ?? null;
          const res = await searchAddress(q, {
            limit: 6,
            ...(bias ? { lat: bias.lat, lon: bias.lng } : {}),
          });
          setResults(res);
          setShowResults(true);
        } catch {
          setResults([]);
        } finally {
          setSearching(false);
        }
      }, 350);
    },
    [position, fallbackCenter]
  );

  const handleSelectResult = (r: ForwardGeocodeResult) => {
    setPosition({ lat: r.lat, lng: r.lon });
    setAddress(r.address);
    setFlyTo({ lat: r.lat, lng: r.lon, zoom: 16 });
    setShowResults(false);
    setQuery(r.address);
  };

  useEffect(() => {
    return () => {
      if (geocodeTimer.current) window.clearTimeout(geocodeTimer.current);
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, []);

  const handleSave = () => {
    if (!position) return;
    onSave({
      lat: position.lat,
      lng: position.lng,
      address,
      radius: radius > 0 ? Math.round(radius) : defaultRadius,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-2 sm:p-3"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-bgElevated rounded-lg shadow-xl flex flex-col overflow-hidden w-[98vw] max-w-[1100px] h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 p-4 border-b border-border flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border rounded">
            {t('common.cancel')}
          </button>
        </div>

        <div className="relative flex-1 min-h-0 w-full bg-bgBase">
          {/* Adressökning (forward geocoding) */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] w-[90%] max-w-[520px]">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  runSearch(e.target.value);
                }}
                onFocus={() => results.length > 0 && setShowResults(true)}
                placeholder={t('charging.config.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-bgElevated shadow-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {searching && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-textSecondary">…</span>
              )}
            </div>
            {showResults && results.length > 0 && (
              <ul className="mt-1 max-h-64 overflow-auto rounded-lg border border-border bg-bgElevated shadow-lg">
                {results.map((r, i) => (
                  <li key={`${r.lat},${r.lon},${i}`}>
                    <button
                      type="button"
                      onClick={() => handleSelectResult(r)}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-bgLight"
                    >
                      {r.address}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <MapContainer center={center} zoom={DEFAULT_ZOOM} className="absolute inset-0 z-0 h-full w-full">
            <TileLayer url="https://app.deque.se/api/map/tiles/{z}/{x}/{y}.png?v=3" attribution="© OpenMapTiles © OpenStreetMap" />
            <MapSizer />
            <MapFlyTo target={flyTo} />
            <ClickHandler onPick={handlePick} />
            {position && (
              <>
                <Marker
                  position={[position.lat, position.lng]}
                  icon={markerIcon}
                  draggable
                  eventHandlers={{
                    dragend: (e) => {
                      const ll = (e.target as L.Marker).getLatLng() as LatLng;
                      handlePick(ll.lat, ll.lng);
                    },
                  }}
                />
                <Circle
                  center={[position.lat, position.lng]}
                  radius={radius > 0 ? radius : defaultRadius}
                  pathOptions={{ color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.15, weight: 2 }}
                />
              </>
            )}
          </MapContainer>
        </div>

        <div className="shrink-0 p-4 border-t border-border space-y-3">
          <p className="text-xs text-textSecondary">{t('charging.config.pickerHelp')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs text-textSecondary mb-1">
                {t('charging.config.address')}
                {geocoding && <span className="ml-2 text-textSecondary">…</span>}
              </label>
              <input
                type="text"
                value={address ?? ''}
                onChange={(e) => setAddress(e.target.value || null)}
                placeholder={t('charging.config.addressPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight"
              />
            </div>
            <div>
              <label className="block text-xs text-textSecondary mb-1">
                {t('charging.config.radius')}
              </label>
              <input
                type="number"
                min={1}
                step="1"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value.replace(',', '.')) || 0)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-textSecondary">
              {position
                ? `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`
                : t('charging.config.pickerNoPoint')}
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border rounded">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!position}
                className="px-3 py-1.5 text-sm bg-primary text-white rounded disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
